import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Mail, Loader2 } from 'lucide-react';
import { io } from 'socket.io-client';
import api from '../services/api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;

const generateId = () => Math.random().toString(36).substring(2, 9);

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [guestInfo, setGuestInfo] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [startingChat, setStartingChat] = useState(false);
  const [connected, setConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('chat_guest');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.name && parsed.email) setGuestInfo(parsed);
      } catch {}
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current && guestInfo) {
      setTimeout(() => inputRef.current.focus(), 400);
    }
  }, [isOpen, guestInfo]);

  const addBotMessage = (text) => {
    setMessages(prev => [...prev, { id: generateId(), type: 'bot', text, timestamp: new Date() }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { id: generateId(), type: 'user', text, timestamp: new Date() }]);
  };

  const connectSocket = (room) => {
    if (socketRef.current) socketRef.current.disconnect();

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join_room', room);
    });

    socket.on('disconnect', () => setConnected(false));

    socket.on('receive_message', (data) => {
      if (data.room === room && data.senderRole === 'admin') {
        setMessages(prev => [...prev, {
          id: generateId(),
          type: 'bot',
          text: data.message,
          timestamp: new Date(data.timestamp),
        }]);
      }
    });

    socket.on('connect_error', () => setConnected(false));
  };

  useEffect(() => {
    return () => { if (socketRef.current) socketRef.current.disconnect(); };
  }, []);

  useEffect(() => {
    if (guestInfo?.room) connectSocket(guestInfo.room);
  }, [guestInfo?.room]);

  const startChat = async () => {
    if (!nameInput.trim() || !emailInput.trim() || !emailInput.includes('@')) return;
    setStartingChat(true);
    const name = nameInput.trim();
    const email = emailInput.trim().toLowerCase();
    const room = `guest_${email.replace(/[^a-z0-9]/gi, '_')}`;
    const info = { name, email, room };
    setGuestInfo(info);
    localStorage.setItem('chat_guest', JSON.stringify(info));

    try {
      const res = await api.get(`/chat/${room}`);
      if (res.data.messages && res.data.messages.length > 0) {
        setMessages(res.data.messages.map(m => ({
          id: m._id,
          type: m.senderRole === 'admin' ? 'bot' : 'user',
          text: m.message,
          timestamp: new Date(m.createdAt),
        })));
      } else {
        addBotMessage(`Hi ${name}! Welcome to Advance Advert Tv Digital Support. How can we help you today?`);
      }
    } catch {
      addBotMessage(`Hi ${name}! Welcome to Advance Advert Tv Digital Support. How can we help you today?`);
    }

    setStartingChat(false);
    setTimeout(() => inputRef.current?.focus(), 400);
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || !guestInfo) return;
    addUserMessage(text);
    setInputValue('');

    try {
      await api.post('/chat/guest', {
        name: guestInfo.name,
        email: guestInfo.email,
        message: text,
        room: guestInfo.room,
      });
    } catch {}
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (guestInfo) handleSend();
      else startChat();
    }
  };

  const handleLogout = () => {
    if (socketRef.current) socketRef.current.disconnect();
    localStorage.removeItem('chat_guest');
    setGuestInfo(null);
    setMessages([]);
    setNameInput('');
    setEmailInput('');
    setConnected(false);
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, scale: 0.8, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }} transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="fixed bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 h-[500px] max-h-[70vh] bg-gray-900 rounded-2xl shadow-2xl border border-gray-800/50 z-[9998] flex flex-col overflow-hidden">

            <div className="bg-gradient-to-r from-primary-600 to-accent-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Advance Advert Tv Digital Support</h3>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></span>
                    <span className="text-white/80 text-xs">{connected ? 'Online' : 'Connecting...'}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {guestInfo && (
                  <button onClick={handleLogout} className="text-white/60 hover:text-white text-xs px-2 py-1 rounded transition-colors">Switch</button>
                )}
                <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white transition-colors p-1"><X size={20} /></button>
              </div>
            </div>

            {!guestInfo ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gray-950/50">
                {startingChat ? (
                  <Loader2 size={28} className="animate-spin text-primary-400 mb-4" />
                ) : (
                  <>
                    <div className="w-14 h-14 bg-primary-500/20 rounded-2xl flex items-center justify-center mb-4">
                      <MessageCircle size={24} className="text-primary-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-1">Start a Conversation</h3>
                    <p className="text-gray-500 text-sm text-center mb-5">Enter your details to chat with our support team</p>
                    <div className="w-full space-y-3">
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                        <input type="text" value={nameInput} onChange={e => setNameInput(e.target.value)} onKeyDown={handleKeyDown}
                          placeholder="Your name"
                          className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all" />
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                        <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} onKeyDown={handleKeyDown}
                          placeholder="Your email"
                          className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all" />
                      </div>
                      <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        onClick={startChat} disabled={!nameInput.trim() || !emailInput.trim() || !emailInput.includes('@')}
                        className="w-full py-3 bg-gradient-to-r from-primary-600 to-accent-600 text-white font-semibold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                        Start Chat
                      </motion.button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-950/50">
                  <AnimatePresence>
                    {messages.map((msg) => (
                      <motion.div key={msg.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                        className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.type === 'bot' && (
                          <div className="w-7 h-7 bg-gray-800 rounded-full flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                            <Bot size={14} className="text-primary-400" />
                          </div>
                        )}
                        <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                          msg.type === 'user' ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-br-md' : 'bg-gray-800 text-gray-200 rounded-bl-md'
                        }`}>
                          <p className="text-sm leading-relaxed">{msg.text}</p>
                        </div>
                        {msg.type === 'user' && (
                          <div className="w-7 h-7 bg-gray-800 rounded-full flex items-center justify-center ml-2 flex-shrink-0 mt-1">
                            <User size={14} className="text-accent-400" />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-3 border-t border-gray-800 bg-gray-900">
                  <div className="flex items-center gap-2">
                    <input ref={inputRef} type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown} placeholder="Type a message..."
                      className="flex-1 bg-gray-800 text-gray-200 placeholder-gray-500 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 border border-gray-700 focus:border-primary-500 transition-all" />
                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                      onClick={handleSend} disabled={!inputValue.trim()}
                      className="w-10 h-10 bg-gradient-to-r from-primary-600 to-accent-600 rounded-full flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-primary-500/25 transition-all">
                      <Send size={16} />
                    </motion.button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1, type: 'spring', stiffness: 200 }}
        onClick={() => setIsOpen(!isOpen)} className="fixed bottom-6 right-4 sm:right-6 z-[9999] group">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full blur-lg opacity-60 group-hover:opacity-80 animate-pulse transition-opacity"></div>
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          className="relative w-14 h-14 bg-gradient-to-r from-primary-600 to-accent-600 rounded-full flex items-center justify-center shadow-xl shadow-primary-500/30">
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <X size={24} className="text-white" />
              </motion.div>
            ) : (
              <motion.div key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <MessageCircle size={24} className="text-white" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.button>
    </>
  );
};

export default ChatBubble;
